import {useAddComment, CommentsCategory} from '@domain/comments'
import {KpiType} from '@hconnect/common/types'
import {Button, H4} from '@hm/fundamental'
import {mdiPlus} from '@mdi/js'
import {Moment} from 'moment-timezone'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE, PERFORMANCE_REVIEW_KPI_TYPES} from '../consts'
import {PerformanceReviewQueryKey} from '../types'
import {getRegionalManagerCommentsCategoryInstanceId} from '../utils'

import {AddPerformanceReviewCommentContainer} from './AddPerformanceReviewCommentContainer'
import {RegionalManagerKpisCommentsContainer} from './RegionalManagerKpisCommentsContainer'

type Props = {
  plantId: string
  selectedDate: Moment
  selectedCommentsKpiTypes: KpiType[]
  onSelectedCommentsKpiTypesChange: (kpiTypes: KpiType[]) => void
}

const ALL_COMMENTS_KPI_TYPES = PERFORMANCE_REVIEW_KPI_TYPES as unknown as KpiType[]

export const KpiYearMonthCommentsContainer = ({
  plantId,
  selectedDate,
  selectedCommentsKpiTypes,
  onSelectedCommentsKpiTypesChange
}: Props) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [showAddCommentForm, setShowAddCommentForm] = useState(false)

  const kpiTypesToProcess = selectedCommentsKpiTypes.length
    ? selectedCommentsKpiTypes
    : ALL_COMMENTS_KPI_TYPES
  const commentCategoryInstanceIds = kpiTypesToProcess.map((kpiType) =>
    getRegionalManagerCommentsCategoryInstanceId(kpiType, selectedDate)
  )

  const {mutateAsync: addComment} = useAddComment(
    {
      plantId,
      commentsCategory: CommentsCategory.HrocRegionalManagerKpis
    },
    PerformanceReviewQueryKey.RegionalManagerKpisComments
  )

  const handleAddComment = async (comment: string, kpiType: KpiType) => {
    await addComment({
      value: comment,
      commentCategoryInstanceId: getRegionalManagerCommentsCategoryInstanceId(kpiType, selectedDate)
    })

    // If there is filtering by KPI types, and the added comment's KPI type is not included,
    if (selectedCommentsKpiTypes.length > 0 && !selectedCommentsKpiTypes.includes(kpiType)) {
      // Ensure the KPI type is included in the selected comments KPI types
      const updatedKpiTypes = [...new Set([...selectedCommentsKpiTypes, kpiType])]
      onSelectedCommentsKpiTypesChange(updatedKpiTypes)
    }

    setShowAddCommentForm(false)
  }

  const handleCancelAddComment = () => {
    setShowAddCommentForm(false)
  }

  const handleAddCommentClick = () => {
    setShowAddCommentForm(true)
  }

  return (
    <div data-test-id="kpi-comments-section">
      <div className="flex justify-between items-center mb-4">
        <H4>{t('comments.cardTitle')}</H4>
        <Button
          onClick={handleAddCommentClick}
          icon={mdiPlus}
          variant="tertiary"
          data-cy="add-comment-button"
        >
          {t('comments.label.addComment')}
        </Button>
      </div>
      <AddPerformanceReviewCommentContainer
        isOpen={showAddCommentForm}
        onAddComment={handleAddComment}
        onCancel={handleCancelAddComment}
      />
      <RegionalManagerKpisCommentsContainer
        plantId={plantId}
        commentCategoryInstanceIds={commentCategoryInstanceIds}
        selectedCommentsKpiTypes={selectedCommentsKpiTypes}
        onSelectedCommentsKpiTypesChange={onSelectedCommentsKpiTypesChange}
      />
    </div>
  )
}
